import { call, put, select, takeLatest } from "redux-saga/effects";

import * as apis from "../../network/apis/companies";
import * as types from "./types";
import * as actions from "./actions";
import messages from "../../assets/locale/messages";
import { showErrorMsg, showSuccessMsg } from "../../utils/Helpers";
import { getPortalApiErrorMessage } from "../../utils/companyPortal/eventApi";
import { buildCompaniesListApiParams } from "./companiesListApiParams";
import { normalizeCompanyEditRequestResponse } from "./companyEditRequestNormalize";

/** Fetches a single company by id; lives here (not registration sagas) — still updates `companiesRegistration` slice */
function* getCompanyDetailsSaga({ payload }) {
  const { id, force } = payload;
  if (!force) {
    const companiesRegistration = yield select(
      (state) => state.companiesRegistration
    );
    const alreadyHave =
      companiesRegistration.companyDetails &&
      String(companiesRegistration.companyDetailsRequestId) === String(id);
    if (alreadyHave) {
      return;
    }
  }
  try {
    const bustCache = Boolean(force);
    const response = yield call(
      apis.getCompanyById,
      id,
      bustCache
        ? {
            params: { _t: Date.now() },
            headers: {
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
          }
        : {},
    );
    const raw = response.data?.data ?? response.data;
    yield put(
      actions.getCompanyDetailsSuccess({
        id,
        data: raw,
      })
    );
  } catch (error) {
    const message = error.response?.data?.message || "An error occurred";
    yield put(
      actions.getCompanyDetailsFailure({
        error: message,
        id,
      })
    );
    yield error.response && showErrorMsg(message);
  }
}

function* getCompaniesListSaga() {
  try {
    const { companiesList } = yield select((s) => s);
    const query = buildCompaniesListApiParams(companiesList);
    const response = yield call(apis.getCompanies, query);
    const raw = response.data?.data ?? response.data;
    const list = Array.isArray(raw) ? raw : [];
    const meta = apis.metaFromCompaniesResponse(response);

    yield put(actions.getCompaniesListSuccess({ list, meta }));

    const { pages, currentPage } = meta;
    if (pages > 0 && currentPage > pages) {
      yield put(actions.setCompaniesListPage(pages));
      yield put(actions.getCompaniesListRequest());
    }
  } catch (error) {
    const message = error.response?.data?.message || "An error occurred";
    yield put(actions.getCompaniesListFailure({ error: message }));
    if (error.response) showErrorMsg(message);
  }
}

function* getCompanyEditRequestShowSaga({ payload }) {
  const { id } = payload;
  try {
    const response = yield call(apis.getCompanyEditRequestById, id, {
      // The review page shows its own inline spinner while this loads, so the
      // global dimmed loader (which would stack a second, white one) is skipped.
      handlerEnabled: false,
      params: { _t: Date.now() },
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });
    const data = normalizeCompanyEditRequestResponse(response);
    if (!data) {
      throw new Error("Empty response");
    }
    yield put(actions.getCompanyEditRequestShowSuccess({ data }));
  } catch (error) {
    const d = error.response?.data;
    const message = Array.isArray(d?.errors)
      ? d.errors[0]
      : d?.message || error.message || "An error occurred";
    yield put(actions.getCompanyEditRequestShowFailure({ error: message }));
    yield error.response && showErrorMsg(message);
  }
}

function* patchCompanyEditRequestSaga({ payload }) {
  const {
    editRequestId,
    companyRouteId,
    status,
    rejectionReason,
    onSuccess,
    onFailure,
  } = payload;
  try {
    const body =
      status === "rejected"
        ? { status, rejection_reason: rejectionReason ?? "" }
        : { status };
    yield call(apis.patchCompanyEditRequest, editRequestId, body);
    yield put(actions.patchCompanyEditRequestSuccess());
    yield put(actions.clearCompanyEditRequestDetail());
    yield put(
      actions.getCompanyDetailsRequest({ id: companyRouteId, force: true }),
    );
    yield showSuccessMsg(
      status === "approved"
        ? "companyEditRequestApproved"
        : "companyEditRequestRejected",
    );
    if (typeof onSuccess === "function") {
      onSuccess();
    }
  } catch (error) {
    yield put(actions.patchCompanyEditRequestFailure());
    const d = error.response?.data;
    const message = Array.isArray(d?.errors)
      ? d.errors[0]
      : d?.message || "An error occurred";
    if (error.response) showErrorMsg(message);
    if (typeof onFailure === "function") {
      onFailure(error);
    }
  }
}

function* updateCompanySaga({ payload }) {
  const { id, data, onSuccess } = payload;
  try {
    yield call(apis.patchCompanyAdmin, id, data);
    yield put(actions.updateCompanySuccess());
    yield put(actions.closeEditCompanyDrawer());

    const existing = yield select(
      (state) => state.companiesRegistration.companyDetails,
    );
    const existingReqId = yield select(
      (state) => state.companiesRegistration.companyDetailsRequestId,
    );
    const sentUrl =
      data &&
      typeof data === "object" &&
      typeof data.commercial_record_url === "string" &&
      data.commercial_record_url.length > 0
        ? data.commercial_record_url
        : null;
    const sentClearCr =
      data &&
      typeof data === "object" &&
      data.commercial_record_url === "";
    if (existing && String(existingReqId) === String(id)) {
      if (sentUrl) {
        yield put(
          actions.getCompanyDetailsSuccess({
            id,
            data: { ...existing, commercial_record_url: sentUrl },
          }),
        );
      } else if (sentClearCr) {
        yield put(
          actions.getCompanyDetailsSuccess({
            id,
            data: { ...existing, commercial_record_url: null },
          }),
        );
      }
    }

    yield put(actions.getCompanyDetailsRequest({ id, force: true }));
    yield put(actions.getCompaniesListRequest());
    yield showSuccessMsg("companyUpdatedSuccessfully");
    if (typeof onSuccess === "function") onSuccess();
  } catch (error) {
    const message = error.response?.data?.message || "An error occurred";
    yield put(actions.updateCompanyFailure({ error: message }));
    if (error.response) showErrorMsg(message);
  }
}

/** Past the status filter the row no longer belongs in the list: reload it, for the right rows and totals. */
function* reloadListIfFilteredOut(company) {
  const { status } = yield select((state) => state.companiesList.filters);
  if (status?.length && !status.includes(company.status)) {
    yield put(actions.getCompaniesListRequest());
  }
}

/** The toast is built from the response; on a failure nothing changed and the modal stays open. */
export function* deactivateCompanySaga({ payload }) {
  const { id, reason, onSuccess } = payload;
  const lang = yield select((state) => state.locale.lang);
  const { companiesList, shared } = messages[lang];
  const t = companiesList.deactivation;
  try {
    const response = yield call(apis.deactivateCompany, id, reason, {
      handlerEnabled: false,
    });
    const company = response.data.data;
    const count = company.number_of_cancelled_events_by_deactivation;
    yield put(actions.deactivateCompanySuccess(company));
    yield call(reloadListIfFilteredOut, company);
    yield call(
      showSuccessMsg,
      count === 1
        ? t.successMessageOne
        : t.successMessage.replace("{count}", Number(count).toLocaleString("en-US")),
      true,
      t.successTitle.replace("{company}", company.name),
    );
    if (typeof onSuccess === "function") onSuccess();
  } catch (error) {
    yield put(actions.deactivateCompanyFailure());
    yield call(showErrorMsg, getPortalApiErrorMessage(error, shared.generalErrMsg));
  }
}

/** On a failure the company stays deactivated and the modal stays open. */
export function* activateCompanySaga({ payload }) {
  const { id, onSuccess } = payload;
  const lang = yield select((state) => state.locale.lang);
  const { companiesList, shared } = messages[lang];
  const t = companiesList.activation;
  try {
    const response = yield call(apis.activateCompany, id, {
      handlerEnabled: false,
    });
    const company = response.data.data;
    yield put(actions.activateCompanySuccess(company));
    yield call(reloadListIfFilteredOut, company);
    yield call(
      showSuccessMsg,
      t.successMessage,
      true,
      t.successTitle.replace("{company}", company.name),
    );
    if (typeof onSuccess === "function") onSuccess();
  } catch (error) {
    yield put(actions.activateCompanyFailure());
    yield call(showErrorMsg, getPortalApiErrorMessage(error, shared.generalErrMsg));
  }
}

export default function* companiesListSagas() {
  yield takeLatest(types.GET_COMPANIES_LIST_REQUEST, getCompaniesListSaga);
  yield takeLatest(types.UPDATE_COMPANY_REQUEST, updateCompanySaga);
  yield takeLatest(types.DEACTIVATE_COMPANY_REQUEST, deactivateCompanySaga);
  yield takeLatest(types.ACTIVATE_COMPANY_REQUEST, activateCompanySaga);
  yield takeLatest(types.GET_COMPANY_DETAILS_REQUEST, getCompanyDetailsSaga);
  yield takeLatest(
    types.GET_COMPANY_EDIT_REQUEST_SHOW_REQUEST,
    getCompanyEditRequestShowSaga,
  );
  yield takeLatest(
    types.PATCH_COMPANY_EDIT_REQUEST_REQUEST,
    patchCompanyEditRequestSaga,
  );
}
